/**
 * ProjectArchiveSearch — search bar on the project page that hits the
 * archive (finalized meetings only). Scoped to the active project.
 *
 * UX:
 *   - Empty query → no results panel, just the input + description.
 *   - Non-empty query → fires GET /meetings/search?projectId=&q= with a
 *     250ms debounce to avoid thrashing the FTS index while typing.
 *   - Results render as a list of meetings with the ts_headline snippet
 *     (matched terms wrapped in <b> by Postgres, rendered safely below).
 *
 * Mockup v4 chrome (.archive-search): bone surface, charcoal/8 border,
 * rounded-xl(12px), padding 16px. Input on cream bg with charcoal/10
 * border. Results: charcoal/10 outer rounded, divided rows with
 * pine-soft hover. <b> highlights inside snippets get a pine-soft
 * background + pine text + tiny radius, so matched terms read as
 * highlighted not just bold.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/apiClient.ts';
import { NO_AUTOFILL } from '../lib/noAutofill.ts';

export interface SearchHit {
  id: string;
  title: string;
  meetingDate: string;
  finalizedAt: string | null;
  ownerUserId: string;
  projectId: string;
  snippet: string;
  rank: number;
}

interface Props {
  projectId: string;
  projectName: string;
}

export function ProjectArchiveSearch({ projectId, projectName }: Props) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');

  // Debounce keystrokes so we don't hit the FTS endpoint on every letter.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const search = useQuery({
    queryKey: ['search', projectId, debounced],
    queryFn: () =>
      api.get<{ results: SearchHit[]; query: string }>('/meetings/search', {
        projectId,
        q: debounced,
      }),
    enabled: debounced.length > 0,
    staleTime: 30_000,
  });

  return (
    <section className="rounded-xl border border-charcoal/10 bg-bone p-4">
      <label className="block">
        <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-pine">
          Search finalized minutes
        </span>
        <input
          {...NO_AUTOFILL}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search topics and bullet points across finalized ${projectName} meetings…`}
          className="w-full rounded-lg border border-charcoal/10 bg-cream px-3.5 py-2 text-[13px] text-charcoal transition-colors placeholder:text-charcoal/45 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
          autoComplete="off"
        />
        <p className="mt-1.5 text-[11px] text-charcoal/55">
          Searches finalized minutes for this project only. Matches topic
          titles and bullet text. Drafts are not included.
        </p>
      </label>

      {debounced.length === 0 ? null : search.isLoading ? (
        <div className="mt-3 text-xs italic text-charcoal/55">Searching…</div>
      ) : search.error ? (
        <div className="mt-3 rounded-lg border border-terra-cotta/30 bg-terra-cotta/10 px-3 py-2 text-xs text-terra-cotta">
          {String((search.error as Error).message)}
        </div>
      ) : search.data && search.data.results.length === 0 ? (
        <div className="mt-3 rounded-lg border border-dashed border-charcoal/20 px-4 py-4 text-center text-xs italic text-charcoal/55">
          No finalized meetings in this project mention &ldquo;{debounced}
          &rdquo;.
        </div>
      ) : search.data ? (
        <ul className="mt-3 overflow-hidden rounded-lg border border-charcoal/10">
          {search.data.results.map((r) => (
            <li
              key={r.id}
              className="border-b border-charcoal/[0.06] last:border-b-0"
            >
              <Link
                to={`/meetings/${r.id}/edit`}
                className="block px-3.5 py-2.5 transition-colors hover:bg-pine/[0.06]"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="truncate text-[13px] font-medium text-charcoal">
                    {r.title || (
                      <span className="italic text-charcoal/55">Untitled</span>
                    )}
                  </div>
                  <div className="shrink-0 text-[11px] tabular-nums text-charcoal/55">
                    {r.meetingDate}
                  </div>
                </div>
                {r.snippet && (
                  <div
                    // Server pre-escapes the snippet and emits safe
                    // <mark>matched term</mark> markup around hits
                    // (security audit H1, see routes/search.ts).
                    // dangerouslySetInnerHTML is safe here because
                    // result.snippet contains only HTML-entity-
                    // escaped text plus our own <mark> tags.
                    className="snippet-highlight mt-1 line-clamp-2 text-[12px] leading-[1.5] text-charcoal/75"
                    dangerouslySetInnerHTML={{ __html: r.snippet }}
                  />
                )}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
